import CryptoJS from 'crypto-js';

/**
 * 🔒 SYSTÈME DE CHIFFREMENT SÉCURISÉ POUR LE FRONTEND
 * 
 * Fonctionnalités:
 * - Chiffrement AES-256 des données sensibles
 * - Clé dérivée de l'empreinte du navigateur
 * - Protection contre la corruption des données
 * - Nettoyage automatique des données corrompues
 * - Compatibilité avec l'existant
 */

class SecureStorage {
  constructor() {
    // Générer une clé unique basée sur l'empreinte du navigateur
    this.key = this.generateKey();
    this.version = '1.0';
    this.prefix = 'secure_nodemented_';
    
    // Test initial de fonctionnement
    this.testEncryption();
  }

  /**
   * Génère une clé de chiffrement basée sur l'empreinte du navigateur
   * Cette clé est unique par navigateur/appareil
   */
  generateKey() {
    try {
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        navigator.languages?.join(',') || '',
        new Date().getTimezoneOffset().toString(),
        screen.width + 'x' + screen.height,
        screen.colorDepth.toString(),
        navigator.platform,
        navigator.cookieEnabled.toString(),
        navigator.onLine.toString(),
        window.devicePixelRatio?.toString() || '1'
      ].join('|');
      
      // Créer un hash SHA-256 de l'empreinte
      const hash = CryptoJS.SHA256(fingerprint + 'nodemented_secret_salt_2025').toString();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 [SecureStorage] Clé de chiffrement générée basée sur empreinte navigateur');
      }
      
      return hash;
    } catch (error) {
      console.error('❌ [SecureStorage] Erreur génération clé:', error);
      // Fallback: clé basique mais toujours unique
      return CryptoJS.SHA256('fallback_key_' + Date.now()).toString();
    }
  }

  /**
   * Test initial du système de chiffrement
   */
  testEncryption() {
    try {
      const testData = { test: 'encryption_test', timestamp: Date.now() };
      const encrypted = this.encrypt(testData);
      const decrypted = this.decrypt(encrypted);
      
      if (JSON.stringify(testData) !== JSON.stringify(decrypted)) {
        throw new Error('Test de chiffrement échoué');
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ [SecureStorage] Test de chiffrement réussi');
      }
    } catch (error) {
      console.error('❌ [SecureStorage] Test de chiffrement échoué:', error);
    }
  }

  /**
   * Chiffre des données avec AES-256
   */
  encrypt(data) {
    try {
      const jsonString = JSON.stringify(data);
      const encrypted = CryptoJS.AES.encrypt(jsonString, this.key).toString();
      
      // Ajouter métadonnées pour validation future
      const payload = {
        data: encrypted,
        version: this.version,
        timestamp: Date.now()
      };
      
      return JSON.stringify(payload);
    } catch (error) {
      console.error('❌ [SecureStorage] Erreur chiffrement:', error);
      return null;
    }
  }

  /**
   * Déchiffre des données AES-256
   */
  decrypt(encryptedPayload) {
    try {
      if (!encryptedPayload) return null;
      
      const payload = JSON.parse(encryptedPayload);
      
      // Vérifier la version pour compatibilité future
      if (payload.version !== this.version) {
        console.warn('⚠️ [SecureStorage] Version différente détectée, tentative de déchiffrement');
      }
      
      const decrypted = CryptoJS.AES.decrypt(payload.data, this.key);
      const jsonString = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!jsonString) {
        throw new Error('Déchiffrement impossible - clé invalide ou données corrompues');
      }
      
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('❌ [SecureStorage] Erreur déchiffrement:', error);
      return null;
    }
  }

  /**
   * Stocke des données chiffrées dans localStorage
   */
  setItem(key, value) {
    try {
      const encrypted = this.encrypt(value);
      if (!encrypted) {
        throw new Error('Chiffrement échoué');
      }
      
      const storageKey = this.prefix + key;
      localStorage.setItem(storageKey, encrypted);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔐 [SecureStorage] Données stockées: ${key}`);
      }
      
      return true;
    } catch (error) {
      console.error('❌ [SecureStorage] Erreur stockage:', error);
      return false;
    }
  }

  /**
   * Récupère des données chiffrées depuis localStorage
   */
  getItem(key) {
    try {
      const storageKey = this.prefix + key;
      const encrypted = localStorage.getItem(storageKey);
      
      if (!encrypted) return null;
      
      const decrypted = this.decrypt(encrypted);
      
      if (!decrypted) {
        // Nettoyer les données corrompues
        console.warn(`⚠️ [SecureStorage] Données corrompues détectées pour ${key}, nettoyage...`);
        this.removeItem(key);
        return null;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔐 [SecureStorage] Données récupérées: ${key}`);
      }
      
      return decrypted;
    } catch (error) {
      console.error('❌ [SecureStorage] Erreur récupération:', error);
      // En cas d'erreur, nettoyer les données potentiellement corrompues
      this.removeItem(key);
      return null;
    }
  }

  /**
   * Supprime des données chiffrées
   */
  removeItem(key) {
    try {
      const storageKey = this.prefix + key;
      localStorage.removeItem(storageKey);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🗑️ [SecureStorage] Données supprimées: ${key}`);
      }
    } catch (error) {
      console.error('❌ [SecureStorage] Erreur suppression:', error);
    }
  }

  /**
   * Nettoie toutes les données chiffrées de cette application
   */
  clear() {
    try {
      const keysToRemove = [];
      
      // Identifier toutes nos clés chiffrées
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.prefix)) {
          keysToRemove.push(key);
        }
      }
      
      // Supprimer toutes nos données
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🧹 [SecureStorage] ${keysToRemove.length} éléments nettoyés`);
      }
    } catch (error) {
      console.error('❌ [SecureStorage] Erreur nettoyage:', error);
    }
  }

  /**
   * Migre des données non chiffrées vers le stockage sécurisé
   */
  migrateFromPlainStorage(oldKey, newKey = null) {
    try {
      const targetKey = newKey || oldKey;
      const plainData = localStorage.getItem(oldKey);
      
      if (!plainData) return false;
      
      // Tenter de parser les données
      let parsedData;
      try {
        parsedData = JSON.parse(plainData);
      } catch {
        // Si ce n'est pas du JSON, stocker tel quel
        parsedData = plainData;
      }
      
      // Stocker de manière sécurisée
      const success = this.setItem(targetKey, parsedData);
      
      if (success) {
        // Supprimer l'ancienne version non chiffrée
        localStorage.removeItem(oldKey);
        console.log(`🔄 [SecureStorage] Migration réussie: ${oldKey} → ${targetKey} (chiffré)`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ [SecureStorage] Erreur migration:', error);
      return false;
    }
  }

  /**
   * Vérifie la sanité du stockage
   */
  healthCheck() {
    try {
      const testKey = 'health_check_test';
      const testData = { 
        test: true, 
        timestamp: Date.now(),
        random: Math.random()
      };
      
      // Test complet: stockage → récupération → vérification
      const stored = this.setItem(testKey, testData);
      if (!stored) return false;
      
      const retrieved = this.getItem(testKey);
      if (!retrieved) return false;
      
      const isValid = JSON.stringify(testData) === JSON.stringify(retrieved);
      
      // Nettoyer le test
      this.removeItem(testKey);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🏥 [SecureStorage] Health check: ${isValid ? 'OK' : 'FAILED'}`);
      }
      
      return isValid;
    } catch (error) {
      console.error('❌ [SecureStorage] Health check échoué:', error);
      return false;
    }
  }
}

// Instance singleton
export const secureStorage = new SecureStorage();

// Utilitaires de migration pour compatibilité
export const migrateAuthData = () => {
  try {
    // Migrer les données d'auth existantes si elles existent
    const oldAuthData = localStorage.getItem('authData');
    if (oldAuthData && !secureStorage.getItem('authData')) {
      console.log('🔄 [Migration] Démarrage migration authData vers stockage sécurisé...');
      const migrated = secureStorage.migrateFromPlainStorage('authData');
      if (migrated) {
        console.log('✅ [Migration] AuthData migré avec succès vers stockage sécurisé');
      } else {
        console.error('❌ [Migration] Échec migration authData');
      }
    }
  } catch (error) {
    console.error('❌ [Migration] Erreur migration authData:', error);
  }
};

// Auto-migration au chargement du module
migrateAuthData();

export default secureStorage;
